// src/components/RechargeSheet.tsx
//
// Recharge bottom sheet — mirrors the app's showRechargeSheet. Opened when the
// user lacks enough wallet balance to unlock a monthly report or remedy. Shows
// the same recharge packages the wallet screen uses.
//
// Gateway order matches the wallet page: Juspay first (utils/juspayCheckout.ts,
// gated by its server-side rollout switch), then the original topup contract
// (create-topup-session → Razorpay) whenever Juspay is not available.

import { useState } from "react";
import toast from "react-hot-toast";
import { useAuth } from "../context/AuthContext";
import { usePricing } from "../context/PricingContext";
import { supabase } from "../supabaseClient";
import { AnalyticsManager, trackEvent } from "../utils/analytics";
import { runJuspayTopup, JuspaySkip } from "../utils/juspayCheckout";
import { IconX, IconWallet } from "@tabler/icons-react";
import { countries } from "../data/countries";
import styles from "./RechargeSheet.module.css";

// Razorpay's contact field defaults to +91 (India) when nothing is prefilled.
// Prefill the dial code for the user's country so foreign users see their own
// flag instead of India (bare code like "+1" is enough for Razorpay to set it).
const dialCodeForCountry = (iso?: string | null): string => {
  if (!iso) return "";
  const c = countries.find((x) => x.iso === iso.toUpperCase());
  // Some entries are like "+1-684"; keep just the leading "+<digits>".
  return c ? (c.code.split("-")[0] || "") : "";
};

// What the customer pays for a pack, in minor units. INR adds 18% GST like the
// wallet page; the sum matches the server's gstMinor, so this total is the total
// charged. Other currencies pay the bare price.
const payableFor = (price: number, currency: string): number =>
  currency.toUpperCase() === "INR"
    ? price + Math.floor((price * 18 + 50) / 100)
    : price;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  source?: string;
  // Called after a successful recharge (e.g. to retry the unlock).
  onRecharged?: () => void;
}

export default function RechargeSheet({
  isOpen,
  onClose,
  title = "Add wallet balance",
  subtitle = "You need a bit more balance to unlock this.",
  source,
  onRecharged,
}: Props) {
  const { user, currency, userCountry, userProfiles, refreshUserStatus, fetchWalletBalanceFromDB } =
    useAuth();
  const { packages, formatPrice } = usePricing();
  const [busyId, setBusyId] = useState<string | null>(null);
  const cur = currency || "INR";
  const isInr = cur.toUpperCase() === "INR";

  const openRazorpay = (options: any) => {
    if (!(window as any).Razorpay) {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => new (window as any).Razorpay(options).open();
      document.body.appendChild(script);
    } else {
      new (window as any).Razorpay(options).open();
    }
  };

  const recharge = async (pkg: { id: string; amount: number; price: number }) => {
    if (busyId) return;
    setBusyId(pkg.id);
    const total = payableFor(pkg.price, cur);
    trackEvent("Recharge Sheet Started", {
      package_id: pkg.id,
      amount_pay: total,
      amount_credit: pkg.amount,
      source: source || "recharge_sheet",
    });
    // Juspay first. INR cards on this sheet show the price with GST, so it asks
    // for that; the server prices it. JuspaySkip, and only that, falls through
    // to Razorpay below.
    try {
      // runJuspayTopup logs this purchase itself, exactly once, including when
      // the payment confirms after this sheet has stopped waiting.
      const r = await runJuspayTopup(
        { packageId: pkg.id, currency: cur, withGst: isInr },
        {
          purchase: {
            userId: user?.id || "",
            value: total,
            items: [
              {
                itemId: String(pkg.id),
                itemName: `Wallet Recharge - ${formatPrice(pkg.amount)}`,
                price: total,
                quantity: 1,
              },
            ],
          },
        },
      );
      if (r.result === "credited") {
        toast.success("Wallet recharged!");
        window.dispatchEvent(new CustomEvent("aura:payment-success"));
        setTimeout(async () => {
          await refreshUserStatus().catch(() => {});
          await fetchWalletBalanceFromDB().catch(() => {});
          setBusyId(null);
          onClose();
          onRecharged?.();
        }, 1500);
      } else {
        setBusyId(null);
        if (r.result === "processing") {
          // Possibly paid (a UPI app opened, a bank page) and not confirmed:
          // never "failed", and never "received" either, since it may not be.
          toast(
            "Payment not confirmed yet. If money left your account, your wallet is credited automatically within about 10 minutes. If you didn't pay, nothing was charged.",
            { icon: "⏳", duration: 9000 },
          );
          fetchWalletBalanceFromDB().catch(() => {});
        } else if (r.result === "not_paid") {
          toast("Payment not completed. Nothing was charged.");
        } else if (r.result === "failed") {
          toast.error("The payment didn't go through. Please try again.");
        }
      }
      return;
    } catch (e) {
      if (!(e instanceof JuspaySkip)) {
        // Something broke after the checkout opened. Never open a second
        // checkout on top of a payment that may have gone through.
        console.error("[recharge-sheet] juspay error after opening:", e);
        setBusyId(null);
        return;
      }
      // Not available for this top-up: carry on with Razorpay.
    }

    try {
      const { data, error } = await supabase.functions.invoke(
        "create-topup-session",
        {
          body: {
            amount: total,
            credit_amount: pkg.amount,
            currency: cur,
            package_id: pkg.id,
          },
        },
      );
      if (error) throw new Error(error.message || "Couldn't start checkout");

      if (data?.gateway === "razorpay" && data?.order) {
        openRazorpay({
          key: data.key_id,
          amount: data.order.amount,
          currency: data.order.currency,
          order_id: data.order.id,
          name: "Aura AI Wallet Recharge",
          description: `Add ${formatPrice(pkg.amount)}`,
          prefill: {
            name: userProfiles?.[0]?.name || "",
            email: user?.email || "",
            // Full phone if we have it (its own code sets the flag); otherwise
            // the user's country dial code so foreigners don't default to India.
            contact: user?.phone || dialCodeForCountry(userCountry),
          },
          theme: { color: "#ed0687" },
          handler: async () => {
            AnalyticsManager.logPurchase(data.order.id, total, cur, [
              {
                itemId: String(pkg.id),
                itemName: `Wallet Recharge - ${formatPrice(pkg.amount)}`,
                price: total,
                quantity: 1,
              },
            ]);
            toast.success("Wallet recharged!");
            window.dispatchEvent(new CustomEvent("aura:payment-success"));
            setTimeout(async () => {
              await refreshUserStatus().catch(() => {});
              await fetchWalletBalanceFromDB().catch(() => {});
              setBusyId(null);
              onClose();
              onRecharged?.();
            }, 1500);
          },
          modal: { ondismiss: () => setBusyId(null) },
        });
      } else if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("Unexpected checkout response");
      }
    } catch (e: any) {
      console.error("[recharge-sheet] failed:", e);
      toast.error(e?.message || "Couldn't start recharge");
      setBusyId(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.sheet} onClick={(e) => e.stopPropagation()}>
        <div className={styles.handle} />
        <button className={styles.close} onClick={onClose} aria-label="Close">
          <IconX size={18} />
        </button>

        <div className={styles.header}>
          <span className={styles.icon}>
            <IconWallet size={22} />
          </span>
          <div>
            <p className={styles.title}>{title}</p>
            <p className={styles.subtitle}>{subtitle}</p>
          </div>
        </div>

        {packages.length === 0 ? (
          <p className={styles.empty}>Recharge options are loading…</p>
        ) : (
          <div className={styles.grid}>
            {packages.map((pkg) => {
              const bonus = pkg.amount > pkg.price;
              return (
                <button
                  key={pkg.id}
                  className={styles.pkg}
                  onClick={() => recharge(pkg)}
                  disabled={!!busyId}
                >
                  {bonus && (
                    <span className={styles.bonusPill}>
                      +{formatPrice(pkg.amount - pkg.price)}
                    </span>
                  )}
                  <span className={styles.pkgCredit}>
                    {formatPrice(pkg.amount)}
                  </span>
                  <span className={styles.pkgPay}>
                    {busyId === pkg.id
                      ? "Starting…"
                      : `Pay ${formatPrice(payableFor(pkg.price, cur))}`}
                  </span>
                </button>
              );
            })}
          </div>
        )}
        {isInr && packages.length > 0 && (
          <p className={styles.gstNote}>Prices include 18% GST.</p>
        )}
      </div>
    </div>
  );
}
